import React, { useState, useEffect } from 'react';
import { Form, FormGroup, FormLabel, FormControl, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { legacyContactStep1Schema } from '../Auth/utils/validationSchema';
import { sanitizers } from '../Auth/utils/inputSanitizers.js';
import {
  createPhoneChangeHandler,
  handlePhoneKeyDown,
  getRawPhoneDigits,
  isValidPhoneNumber
} from '../Auth/utils/phoneFormatter.js';
import { locationApi } from '../Auth/utils/LocationApi.tsx';
import SearchableSelect from '../../common/SearchableSelect/SearchableSelect.tsx';
import { toast } from 'react-toastify';
import InfoIcon from "../../../images/info-icon.svg"
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

interface StepOneFormProps {
  onStepComplete: (formData: any) => void;
}

function StepOneForm({ onStepComplete }: StepOneFormProps) {
    // State for location dropdowns
    const [states, setStates] = useState([]);
    const [primaryCities, setPrimaryCities] = useState([]);
    const [secondaryCities, setSecondaryCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(true);
    const [loadingPrimaryCities, setLoadingPrimaryCities] = useState(false);
    const [loadingSecondaryCities, setLoadingSecondaryCities] = useState(false);

    // Fetch states on component mount
    useEffect(() => {
        const fetchStates = async () => {
            try {
                setLoadingStates(true);
                const response = await locationApi.getAllStates();

                if (response.status === 'Success' && response.states) {
                    setStates(response.states);
                } else {
                    toast.error('Failed to load states. Please refresh the page.');
                }
            } catch (error) {
                console.error('Error fetching states:', error);
                toast.error('Failed to load states. Please refresh the page.');
            } finally {
                setLoadingStates(false);
            }
        };

        fetchStates();
    }, []);

    // Step 1 Formik with centralized validation schema
    const step1Formik = useFormik({
        initialValues: {
            primaryContactName: '',
            primaryEmail: '',
            primaryPhone: '+1',
            primaryRelationship: '',
            primaryAddress1: '',
            primaryAddress2: '',
            primaryState: '',
            primaryCity: '',
            primaryZip: '',
            showSecondary: false,
            secondaryContactName: '',
            secondaryEmail: '',
            secondaryPhone: '+1',
            secondaryRelationship: '',
            secondaryAddress1: '',
            secondaryAddress2: '',
            secondaryState: '',
            secondaryCity: '',
            secondaryZip: '',
        },
        validationSchema: legacyContactStep1Schema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await onStepComplete(values);
            } catch (error) {
                toast.error('An error occurred. Please try again.');
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Create phone change handlers using the utility
    const handlePrimaryPhoneChange = createPhoneChangeHandler(step1Formik, "primaryPhone");
    const handleSecondaryPhoneChange = createPhoneChangeHandler(step1Formik, "secondaryPhone");

    // Load cities by state ID for primary contact
    const loadPrimaryCitiesByStateId = async (stateId: string) => {
        if (!stateId) {
            setPrimaryCities([]);
            return;
        }

        try {
            setLoadingPrimaryCities(true);
            const response = await locationApi.getCitiesByStateId(stateId);

            if (response.status === 'Success' && response.cities) {
                setPrimaryCities(response.cities);
            } else {
                setPrimaryCities([]);
                toast.error('Failed to load cities for selected state.');
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setPrimaryCities([]);
            toast.error('Failed to load cities for selected state.');
        } finally {
            setLoadingPrimaryCities(false);
        }
    };

    // Load cities by state ID for secondary contact
    const loadSecondaryCitiesByStateId = async (stateId: string) => {
        if (!stateId) {
            setSecondaryCities([]);
            return;
        }

        try {
            setLoadingSecondaryCities(true);
            const response = await locationApi.getCitiesByStateId(stateId);

            if (response.status === 'Success' && response.cities) {
                setSecondaryCities(response.cities);
            } else {
                setSecondaryCities([]);
                toast.error('Failed to load cities for selected state.');
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setSecondaryCities([]);
            toast.error('Failed to load cities for selected state.');
        } finally {
            setLoadingSecondaryCities(false);
        }
    };

    // Handle state change for primary contact
    const handlePrimaryStateChange = (stateId: string) => {
        const selectedState = states.find((state: any) => state._id === stateId);

        if (selectedState) {
            step1Formik.setFieldValue("primaryState", selectedState.name);
            step1Formik.setFieldValue("primaryCity", ""); // Reset city when state changes
            step1Formik.setFieldValue("primaryZip", ""); // Clear zip code when state changes
            loadPrimaryCitiesByStateId(stateId);
        } else {
            step1Formik.setFieldValue("primaryState", "");
            step1Formik.setFieldValue("primaryCity", "");
            step1Formik.setFieldValue("primaryZip", "");
            setPrimaryCities([]);
        }
    };

    // Handle state change for secondary contact
    const handleSecondaryStateChange = (stateId: string) => {
        const selectedState = states.find((state: any) => state._id === stateId);

        if (selectedState) {
            step1Formik.setFieldValue("secondaryState", selectedState.name);
            step1Formik.setFieldValue("secondaryCity", ""); // Reset city when state changes
            step1Formik.setFieldValue("secondaryZip", ""); // Clear zip code when state changes
            loadSecondaryCitiesByStateId(stateId);
        } else {
            step1Formik.setFieldValue("secondaryState", "");
            step1Formik.setFieldValue("secondaryCity", "");
            step1Formik.setFieldValue("secondaryZip", "");
            setSecondaryCities([]);
        }
    };

    // Prevent space key for email fields
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
        }
    };

    // Enhanced validation check for step 1 submit
    const validateUniqueContacts = () => {
        if (!step1Formik.values.showSecondary) return true;

        const primaryEmail = step1Formik.values.primaryEmail.toLowerCase().trim();
        const secondaryEmail = step1Formik.values.secondaryEmail.toLowerCase().trim();
        const primaryPhone = getRawPhoneDigits(step1Formik.values.primaryPhone);
        const secondaryPhone = getRawPhoneDigits(step1Formik.values.secondaryPhone);

        if (primaryEmail === secondaryEmail) {
            toast.error("Secondary contact email must be different from primary contact email");
            return false;
        }

        if (primaryPhone === secondaryPhone && primaryPhone.length === 10) {
            toast.error("Secondary contact phone number must be different from primary contact phone number");
            return false;
        }

        return true;
    };

    // Handle step 1 submit with validation
    const handleStep1Submit = async () => {
        const errors = await step1Formik.validateForm();
        if (Object.keys(errors).length > 0) {
            toast.error("Please fill all the required fields correctly");
            step1Formik.setTouched({
                primaryContactName: true,
                primaryEmail: true,
                primaryPhone: true,
                primaryRelationship: true,
                primaryAddress1: true,
                primaryState: true,
                primaryCity: true,
                primaryZip: true,
                ...(step1Formik.values.showSecondary && {
                    secondaryContactName: true,
                    secondaryEmail: true,
                    secondaryPhone: true,
                    secondaryRelationship: true,
                    secondaryAddress1: true,
                    secondaryState: true,
                    secondaryCity: true,
                    secondaryZip: true,
                }),
            });
            return;
        }

        // Additional validation for unique contacts
        if (!validateUniqueContacts()) {
            return;
        }

        step1Formik.handleSubmit();
    };

    return (
        <div className="step-1">
            <h3>
                Who is your Legacy Contact?
                <OverlayTrigger
                    placement="auto"
                    overlay={
                        <Tooltip id="tooltip-info">
                            This is the person who can manage your account if
                            needed.
                        </Tooltip>
                    }
                >
                    <span className="d-inline-block">
                        <img
                            src={InfoIcon}
                            alt="Info"
                            style={{ cursor: "pointer" }}
                        />
                    </span>
                </OverlayTrigger>
            </h3>

            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleStep1Submit();
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        handleStep1Submit();
                    }
                }}
            >
                {/* Primary Contact Name */}
                <FormGroup className="form-group">
                    <FormLabel>Primary Legacy Contact Name *</FormLabel>
                    <FormControl
                        autoFocus
                        type="text"
                        name="primaryContactName"
                        placeholder="Enter Legacy Contact Name"
                        value={step1Formik.values.primaryContactName}
                        onChange={(e) => {
                            const sanitized = sanitizers.name(e.target.value);
                            step1Formik.setFieldValue(
                                "primaryContactName",
                                sanitized
                            );
                        }}
                        className={`form-control p-3`}
                        disabled={step1Formik.isSubmitting}
                    />
                    {step1Formik.touched.primaryContactName &&
                        step1Formik.errors.primaryContactName && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryContactName}
                            </small>
                        )}
                </FormGroup>

                {/* Primary Email */}
                <FormGroup className="form-group">
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl
                        type="email"
                        name="primaryEmail"
                        placeholder="Enter Email Address"
                        value={step1Formik.values.primaryEmail}
                        onChange={(e) => {
                            const sanitized = sanitizers.email(e.target.value);
                            step1Formik.setFieldValue(
                                "primaryEmail",
                                sanitized
                            );
                        }}
                        className={`form-control p-3`}
                        onKeyDown={handleKeyDown} // Prevents spaces
                        disabled={step1Formik.isSubmitting}
                    />
                    {step1Formik.touched.primaryEmail &&
                        step1Formik.errors.primaryEmail && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryEmail}
                            </small>
                        )}
                </FormGroup>

                {/* Primary Phone */}
                <FormGroup className="form-group">
                    <FormLabel>Phone Number *</FormLabel>
                    <FormControl
                        type="text"
                        name="primaryPhone"
                        placeholder="Enter Phone Number"
                        value={step1Formik.values.primaryPhone}
                        onChange={handlePrimaryPhoneChange}
                        onKeyDown={handlePhoneKeyDown}
                        className={`form-control p-3`}
                        disabled={step1Formik.isSubmitting}
                    />
                    {step1Formik.touched.primaryPhone &&
                        step1Formik.errors.primaryPhone && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryPhone}
                            </small>
                        )}
                </FormGroup>

                {/* Primary Relationship */}
                <FormGroup className="form-group">
                    <FormLabel>Relationship to You *</FormLabel>
                    <FormControl
                        type="text"
                        name="primaryRelationship"
                        placeholder="Enter Relationship"
                        value={step1Formik.values.primaryRelationship}
                        onChange={(e) => {
                            const sanitized = sanitizers.name(e.target.value);
                            step1Formik.setFieldValue(
                                "primaryRelationship",
                                sanitized
                            );
                        }}
                        className={`form-control p-3`}
                        disabled={step1Formik.isSubmitting}
                    />
                    {step1Formik.touched.primaryRelationship &&
                        step1Formik.errors.primaryRelationship && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryRelationship}
                            </small>
                        )}
                </FormGroup>

                {/* Primary Address 1 */}
                <FormGroup className="form-group">
                    <FormLabel>Address 1 *</FormLabel>
                    <FormControl
                        type="text"
                        name="primaryAddress1"
                        placeholder="Enter Address"
                        value={step1Formik.values.primaryAddress1}
                        onChange={step1Formik.handleChange}
                        className={`form-control p-3`}
                        disabled={step1Formik.isSubmitting}
                    />
                    {step1Formik.touched.primaryAddress1 &&
                        step1Formik.errors.primaryAddress1 && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryAddress1}
                            </small>
                        )}
                </FormGroup>

                {/* Primary Address 2 */}
                <FormGroup className="form-group">
                    <FormLabel>Address 2</FormLabel>
                    <FormControl
                        type="text"
                        name="primaryAddress2"
                        placeholder="Enter Address Line 2 (Optional)"
                        value={step1Formik.values.primaryAddress2}
                        onChange={step1Formik.handleChange}
                        className={`form-control p-3`}
                        disabled={step1Formik.isSubmitting}
                    />
                    {step1Formik.touched.primaryAddress2 &&
                        step1Formik.errors.primaryAddress2 && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryAddress2}
                            </small>
                        )}
                </FormGroup>

                {/* Primary State Dropdown */}
                <FormGroup className="form-group">
                    <FormLabel>State *</FormLabel>
                    <SearchableSelect
                        value={states.find((state: any) => state.name === step1Formik.values.primaryState)?._id || ''}
                        onChange={handlePrimaryStateChange}
                        options={states.map((state: any) => ({
                            value: state._id,
                            label: state.name
                        }))}
                        placeholder="Select State"
                        searchPlaceholder="Search states..."
                        loading={loadingStates}
                        loadingText="Loading states..."
                        disabled={step1Formik.isSubmitting || loadingStates}
                        isInvalid={step1Formik.touched.primaryState && !!step1Formik.errors.primaryState && step1Formik.values.primaryState === ''}
                        className="legacy-state-select"
                        clearable={true}
                        noOptionsText="No states found"
                    />
                    {step1Formik.touched.primaryState &&
                        step1Formik.errors.primaryState && step1Formik.values.primaryState === '' && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryState}
                            </small>
                        )}
                </FormGroup>

                {/* Primary City Dropdown */}
                <FormGroup className="form-group">
                    <FormLabel>City *</FormLabel>
                    <SearchableSelect
                        value={step1Formik.values.primaryCity}
                        onChange={(value) => {
                            step1Formik.setFieldValue('primaryCity', value);
                        }}
                        options={primaryCities.map((city: any) => ({
                            value: city.name,
                            label: city.name
                        }))}
                        placeholder={
                            !step1Formik.values.primaryState
                                ? "Select state first"
                                : loadingPrimaryCities
                                ? "Loading cities..."
                                : "Select City"
                        }
                        searchPlaceholder="Search cities..."
                        loading={loadingPrimaryCities}
                        loadingText="Loading cities..."
                        disabled={step1Formik.isSubmitting || loadingPrimaryCities || !step1Formik.values.primaryState}
                        isInvalid={step1Formik.touched.primaryCity && !!step1Formik.errors.primaryCity && step1Formik.values.primaryCity === ''}
                        className="legacy-city-select"
                        clearable={true}
                        noOptionsText={!step1Formik.values.primaryState ? 'Select state first' : 'No cities found'}
                    />
                    {step1Formik.touched.primaryCity &&
                        step1Formik.errors.primaryCity && step1Formik.values.primaryCity === '' && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryCity}
                            </small>
                        )}
                </FormGroup>

                {/* Primary Zip Code */}
                <FormGroup className="form-group">
                    <FormLabel>Zip *</FormLabel>
                    <FormControl
                        type="text"
                        name="primaryZip"
                        placeholder="Enter Zip Code"
                        value={step1Formik.values.primaryZip}
                        onChange={(e) => {
                            // Only allow numbers and limit to 5-10 characters
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            step1Formik.setFieldValue("primaryZip", value);
                        }}
                        className={`form-control p-3`}
                        disabled={step1Formik.isSubmitting}
                        maxLength={10}
                    />
                    {step1Formik.touched.primaryZip &&
                        step1Formik.errors.primaryZip && (
                            <small
                                style={{
                                    color: "#dc3545",
                                    display: "block",
                                    marginTop: "4px",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {step1Formik.errors.primaryZip}
                            </small>
                        )}
                </FormGroup>

                {/* Secondary Contact Checkbox */}
                <Form.Check
                    className="mb-4"
                    type="checkbox"
                    id="secondaryContact"
                    label={
                        <span>
                            Would you like to add a Secondary Legacy Contact?
                        </span>
                    }
                    name="showSecondary"
                    checked={step1Formik.values.showSecondary}
                    onChange={(e) => {
                        const isChecked = e.target.checked;
                        step1Formik.setFieldValue("showSecondary", isChecked);

                        // Clear secondary contact fields when unchecking
                        if (!isChecked) {
                            step1Formik.setFieldValue("secondaryContactName", "");
                            step1Formik.setFieldValue("secondaryEmail", "");
                            step1Formik.setFieldValue("secondaryPhone", "+1");
                            step1Formik.setFieldValue("secondaryRelationship", "");
                            step1Formik.setFieldValue("secondaryAddress1", "");
                            step1Formik.setFieldValue("secondaryAddress2", "");
                            step1Formik.setFieldValue("secondaryState", "");
                            step1Formik.setFieldValue("secondaryCity", "");
                            step1Formik.setFieldValue("secondaryZip", "");

                            // Clear any validation errors for secondary fields
                            step1Formik.setFieldTouched("secondaryContactName", false);
                            step1Formik.setFieldTouched("secondaryEmail", false);
                            step1Formik.setFieldTouched("secondaryPhone", false);
                            step1Formik.setFieldTouched("secondaryRelationship", false);
                            step1Formik.setFieldTouched("secondaryAddress1", false);
                            step1Formik.setFieldTouched("secondaryAddress2", false);
                            step1Formik.setFieldTouched("secondaryState", false);
                            step1Formik.setFieldTouched("secondaryCity", false);
                            step1Formik.setFieldTouched("secondaryZip", false);

                            // Clear cities
                            setSecondaryCities([]);
                        }
                    }}
                    disabled={step1Formik.isSubmitting}
                />

                {/* Secondary Contact Fields */}
                {step1Formik.values.showSecondary && (
                    <>
                        <FormGroup className="form-group">
                            <FormLabel>
                                Secondary Legacy Contact Name *
                            </FormLabel>
                            <FormControl
                                type="text"
                                name="secondaryContactName"
                                placeholder="Enter Secondary Contact Name"
                                value={step1Formik.values.secondaryContactName}
                                onChange={(e) => {
                                    const sanitized = sanitizers.name(
                                        e.target.value
                                    );
                                    step1Formik.setFieldValue(
                                        "secondaryContactName",
                                        sanitized
                                    );
                                }}
                                className={`form-control p-3`}
                                disabled={step1Formik.isSubmitting}
                            />
                            {step1Formik.touched.secondaryContactName &&
                                step1Formik.errors.secondaryContactName && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryContactName}
                                    </small>
                                )}
                        </FormGroup>

                        <FormGroup className="form-group">
                            <FormLabel>Email Address *</FormLabel>
                            <FormControl
                                type="email"
                                name="secondaryEmail"
                                placeholder="Enter Email Address"
                                value={step1Formik.values.secondaryEmail}
                                onChange={(e) => {
                                    const sanitized = sanitizers.email(
                                        e.target.value
                                    );
                                    step1Formik.setFieldValue(
                                        "secondaryEmail",
                                        sanitized
                                    );
                                }}
                                className={`form-control p-3`}
                                onKeyDown={handleKeyDown} // Prevents spaces
                                disabled={step1Formik.isSubmitting}
                            />
                            {step1Formik.touched.secondaryEmail &&
                                step1Formik.errors.secondaryEmail && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryEmail}
                                    </small>
                                )}
                        </FormGroup>

                        <FormGroup className="form-group">
                            <FormLabel>Phone Number *</FormLabel>
                            <FormControl
                                type="text"
                                name="secondaryPhone"
                                placeholder="Enter Phone Number"
                                value={step1Formik.values.secondaryPhone}
                                onChange={handleSecondaryPhoneChange}
                                onKeyDown={handlePhoneKeyDown}
                                className={`form-control p-3`}
                                disabled={step1Formik.isSubmitting}
                            />
                            {step1Formik.touched.secondaryPhone &&
                                step1Formik.errors.secondaryPhone && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryPhone}
                                    </small>
                                )}
                        </FormGroup>

                        <FormGroup className="form-group">
                            <FormLabel>Relationship to You *</FormLabel>
                            <FormControl
                                type="text"
                                name="secondaryRelationship"
                                placeholder="Enter Relationship"
                                value={step1Formik.values.secondaryRelationship}
                                onChange={(e) => {
                                    const sanitized = sanitizers.name(
                                        e.target.value
                                    );
                                    step1Formik.setFieldValue(
                                        "secondaryRelationship",
                                        sanitized
                                    );
                                }}
                                className={`form-control p-3`}
                                disabled={step1Formik.isSubmitting}
                            />
                            {step1Formik.touched.secondaryRelationship &&
                                step1Formik.errors.secondaryRelationship && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryRelationship}
                                    </small>
                                )}
                        </FormGroup>

                        {/* Secondary Address 1 */}
                        <FormGroup className="form-group">
                            <FormLabel>Address 1 *</FormLabel>
                            <FormControl
                                type="text"
                                name="secondaryAddress1"
                                placeholder="Enter Address"
                                value={step1Formik.values.secondaryAddress1}
                                onChange={step1Formik.handleChange}
                                className={`form-control p-3`}
                                disabled={step1Formik.isSubmitting}
                            />
                            {step1Formik.touched.secondaryAddress1 &&
                                step1Formik.errors.secondaryAddress1 && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryAddress1}
                                    </small>
                                )}
                        </FormGroup>

                        {/* Secondary Address 2 */}
                        <FormGroup className="form-group">
                            <FormLabel>Address 2</FormLabel>
                            <FormControl
                                type="text"
                                name="secondaryAddress2"
                                placeholder="Enter Address Line 2 (Optional)"
                                value={step1Formik.values.secondaryAddress2}
                                onChange={step1Formik.handleChange}
                                className={`form-control p-3`}
                                disabled={step1Formik.isSubmitting}
                            />
                            {step1Formik.touched.secondaryAddress2 &&
                                step1Formik.errors.secondaryAddress2 && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryAddress2}
                                    </small>
                                )}
                        </FormGroup>

                        {/* Secondary State Dropdown */}
                        <FormGroup className="form-group">
                            <FormLabel>State *</FormLabel>
                            <SearchableSelect
                                value={states.find((state: any) => state.name === step1Formik.values.secondaryState)?._id || ''}
                                onChange={handleSecondaryStateChange}
                                options={states.map((state: any) => ({
                                    value: state._id,
                                    label: state.name
                                }))}
                                placeholder="Select State"
                                searchPlaceholder="Search states..."
                                loading={loadingStates}
                                loadingText="Loading states..."
                                disabled={step1Formik.isSubmitting || loadingStates}
                                isInvalid={step1Formik.touched.secondaryState && !!step1Formik.errors.secondaryState && step1Formik.values.secondaryState === ''}
                                className="legacy-state-select"
                                clearable={true}
                                noOptionsText="No states found"
                            />
                            {step1Formik.touched.secondaryState &&
                                step1Formik.errors.secondaryState && step1Formik.values.secondaryState === '' && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryState}
                                    </small>
                                )}
                        </FormGroup>

                        {/* Secondary City Dropdown */}
                        <FormGroup className="form-group">
                            <FormLabel>City *</FormLabel>
                            <SearchableSelect
                                value={step1Formik.values.secondaryCity}
                                onChange={(value) => {
                                    step1Formik.setFieldValue('secondaryCity', value);
                                }}
                                options={secondaryCities.map((city: any) => ({
                                    value: city.name,
                                    label: city.name
                                }))}
                                placeholder={
                                    !step1Formik.values.secondaryState
                                        ? "Select state first"
                                        : loadingSecondaryCities
                                        ? "Loading cities..."
                                        : "Select City"
                                }
                                searchPlaceholder="Search cities..."
                                loading={loadingSecondaryCities}
                                loadingText="Loading cities..."
                                disabled={step1Formik.isSubmitting || loadingSecondaryCities || !step1Formik.values.secondaryState}
                                isInvalid={step1Formik.touched.secondaryCity && !!step1Formik.errors.secondaryCity && step1Formik.values.secondaryCity === ''}
                                className="legacy-city-select"
                                clearable={true}
                                noOptionsText={!step1Formik.values.secondaryState ? 'Select state first' : 'No cities found'}
                            />
                            {step1Formik.touched.secondaryCity &&
                                step1Formik.errors.secondaryCity && step1Formik.values.secondaryCity === '' && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryCity}
                                    </small>
                                )}
                        </FormGroup>

                        {/* Secondary Zip Code */}
                        <FormGroup className="form-group">
                            <FormLabel>Zip *</FormLabel>
                            <FormControl
                                type="text"
                                name="secondaryZip"
                                placeholder="Enter Zip Code"
                                value={step1Formik.values.secondaryZip}
                                onChange={(e) => {
                                    // Only allow numbers and limit to 5-10 characters
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    step1Formik.setFieldValue("secondaryZip", value);
                                }}
                                className={`form-control p-3`}
                                disabled={step1Formik.isSubmitting}
                                maxLength={10}
                            />
                            {step1Formik.touched.secondaryZip &&
                                step1Formik.errors.secondaryZip && (
                                    <small
                                        style={{
                                            color: "#dc3545",
                                            display: "block",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {step1Formik.errors.secondaryZip}
                                    </small>
                                )}
                        </FormGroup>
                    </>
                )}

                <Button
                    className="main-bttn mb-5"
                    type="submit"
                    disabled={step1Formik.isSubmitting}
                >
                    {step1Formik.isSubmitting
                        ? "Adding..."
                        : "Add Legacy Contact"}
                </Button>
            </Form>
        </div>
    );
}

export default StepOneForm;
